import { useEffect, useState, useRef } from "react";
import type { LoaderFunction, MetaFunction } from "@remix-run/node";
import { useLoaderData } from "@remix-run/react";
import FeaturedProducts from "app/components/Home/FeaturedProducts";
import ContactUs from "app/components/Home/Contact-us";
import { fetchProductShowCase, fetchHomePageInitialData } from "app/api/product/products.api";
import { fetchAllBlogsApi } from "app/api/blog.api";
import axios from "axios";
import { description, homePageSchema } from "./_index_seo";
import { useQuery } from "@tanstack/react-query";
import { getSession } from "app/auth/sessions";
import { UserType } from "app/api/user/user.types";
import { SEOHandle } from "@nasa-gcn/remix-seo";
import { getSocialMetaTags } from "app/utils/meta";
import { HeroBanner } from "app/components/Home/HeroBanner";
import { WhatsHot } from "app/components/Home/WhatsHot";
import { ProtectedSection } from "app/components/Home/ProtectedSection";
import { BlogSection } from "app/components/Home/BlogSection";
import { ProductShowcaseSection } from "app/components/Home/ProductShowcaseSection";
import { homeComponent } from "app/api/product/product.type";

export const handle: SEOHandle = {
  getSitemapEntries: async (request) => {
    const entries = [];

    entries.push(
      {
        route: "/",
        priority: 1.0 as const,
        lastmod: new Date().toISOString(),
        changefreq: "daily" as const,
      },
      {
        route: "/sitemap-categories.xml",
        priority: 0.8 as const,
        lastmod: new Date().toISOString(),
        changefreq: "weekly" as const,
      },
      {
        route: "/sitemap-products.xml",
        priority: 0.7 as const,
        lastmod: new Date().toISOString(),
        changefreq: "weekly" as const,
      },
    );

    return entries;
  },
};

export const meta: MetaFunction = () =>
  getSocialMetaTags({
    title: "Custom Promotional Products & Corporate Gifts Australia",
    description,
    url: "https://www.promotionalproductsnow.au/",
    extra: [
      { tagName: "link", rel: "canonical", href: "https://www.promotionalproductsnow.au/" },
      {
        name: "keyword",
        content:
          "Promotional products Australia, Custom promotional items,Branded corporate gifts, Personalised merchandise, Logo printed products",
      },
    ],
  });

export function headers() {
  return { "cache-control": "max-age=120, stale-while-revalidate=86400" };
}

export const loader: LoaderFunction = async ({ request }) => {
  const session = await getSession(request.headers.get("Cookie"));
  const uid = session.get("uid");
  const accessToken = session.get("accessToken");
  const userType = session.get("userType");

  try {
    // Fetch all initial home page data from a single endpoint
    const initialData = await fetchHomePageInitialData({
      userType: userType || UserType.Non_VIP,
      page: 1,
      limit: 12,
    });

    if (!uid || !accessToken) {
      return {
        user: null,
        banners: initialData.banners,
        sideAdverts: initialData.sideAdverts ?? [],
        products: initialData.products,
        productShowCase: initialData.productShowCase,
        nextCursor: initialData.nextCursor,
      };
    }
    return {
      user: { uid, userType },
      banners: initialData.banners,
      sideAdverts: initialData.sideAdverts ?? [],
      products: initialData.products,
      productShowCase: initialData.productShowCase,
      nextCursor: initialData.nextCursor,
    };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Response(error.message, {
        status: error.response?.status || 500,
        statusText: error.response?.statusText || "Internal Server Error",
      });
    }
    console.error({ error });
    throw new Response("Internal Server Error", {
      status: 500,
      statusText: "An unexpected error occurred",
    });
  }
};

// Memoized components for performance

export default function Index() {
  const [loadBlog, setLoadBlog] = useState(false);
  const [loadTrending, setLoadTrending] = useState(false);
  const blogTriggerRef = useRef<HTMLDivElement | null>(null);
  const trendingTriggerRef = useRef<HTMLDivElement | null>(null);

  const {
    data: blogs,
    isLoading: isBlogLoading,
    isSuccess: isBlogSuccess,
  } = useQuery({
    queryKey: ["blogs"],
    queryFn: () => fetchAllBlogsApi({ limit: 6 }),
    refetchOnMount: false,
    enabled: loadBlog,
    staleTime: Infinity,
  });

  const loaderData = useLoaderData<typeof loader>();

  // State for productShowCase pagination using cursor
  const [cursor, setCursor] = useState(loaderData.nextCursor);
  const [isFetching, setIsFetching] = useState(false);
  const [productShowCaseData, setProductShowCaseData] = useState(loaderData.productShowCase);
  // Error state for infinite scroll
  const [fetchError, setFetchError] = useState<string | null>(null);
  // A sentinel ref for infinite scroll
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // const [recentlyViewedRaw] = useAtom(recentlyViewedProductsAtom);

  // const recentlyViewed: ProductCardProps[] = recentlyViewedRaw.map((product: any) => ({
  //   image: product.overview?.heroImage || "",
  //   images: product.images || [],
  //   productCode: product.productCode || "",
  //   title: product.name || product.title || "",
  //   labels: product.labels || [],
  //   description: product.description || "",
  //   basePrice: product.basePrice || { min: 0, max: 0 },
  //   qty: product.qty || { min: 0, max: 0 },
  //   category: product.category || "",
  //   subCatgory: product.subCategory || "",
  //   slug: product.slug || "",
  //   isSpecialSupplier: product.supplier?.isSpecialSupplier || false,
  // }));

  // Function to load the next set of items using the cursor
  const loadMore = async () => {
    if (!cursor) {
      setIsFetching(false);
      return;
    } // No more pages to load.

    setIsFetching(true);
    setFetchError(null);
    try {
      const newDataResponse = await fetchProductShowCase({
        userType: loaderData.user?.userType || UserType.Non_VIP,
        cursor,
      }).then((res) => res.data);
      if (newDataResponse && newDataResponse.items.length > 0) {
        setProductShowCaseData((prev: any) => [...prev, ...(newDataResponse?.items || [])]);
        setCursor(newDataResponse.nextCursor);
      } else {
        setCursor(null);
      }
    } catch (error) {
      setFetchError("Failed to load more products. Please try again.");
      console.error("Error fetching next page", error);
    }
    setIsFetching(false);
  };

  // Intersection Observer for Blog Section
  useEffect(() => {
    const blogObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setLoadBlog(true);
            blogObserver.disconnect();
          }
        });
      },
      { threshold: 0.1 },
    );
    if (blogTriggerRef.current) {
      blogObserver.observe(blogTriggerRef.current);
    }
    return () => blogObserver.disconnect();
  }, []);

  // Intersection Observer for Trending Section
  useEffect(() => {
    const trendingObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setLoadTrending(true);
            trendingObserver.disconnect();
          }
        });
      },
      { threshold: 0.1 },
    );
    if (trendingTriggerRef.current) {
      trendingObserver.observe(trendingTriggerRef.current);
    }
    return () => trendingObserver.disconnect();
  }, []);

  // Intersection Observer for Infinite Scroll using the sentinel element.
  useEffect(() => {
    if (isFetching || fetchError) return;
    const observer = new IntersectionObserver(
      (entries) => {
        // When the sentinel comes into view, load more data.
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { threshold: 0.1 },
    );
    if (sentinelRef.current) {
      observer.observe(sentinelRef.current);
    }
    return () => {
      if (sentinelRef.current) {
        observer.unobserve(sentinelRef.current);
      }
    };
  }, [isFetching, productShowCaseData, cursor, fetchError]);

  const { banners, sideAdverts = [], products } = loaderData || {};

  // Calculate mixed sections count from the combined productShowCaseData
  const mixedCount = productShowCaseData.filter(
    (component: homeComponent) => component.type === "mixed",
  ).length;

  return (
    <>
      {/* Hero: main banner + side adverts (side adverts column on lg+, or grid when no banner) */}
      <HeroBanner banners={banners} sideAdverts={sideAdverts} />

      <WhatsHot products={products} />

      {/* Sentinel for lazy-loading trending products */}
      <div ref={trendingTriggerRef} aria-hidden="true" />

      <div className="md:px-0 flex flex-col space-y-10 pt-4 pb-8">
        <FeaturedProducts
          gridNo={5}
          userType={loaderData?.user?.userType}
          loadTrending={loadTrending}
        />
      </div>

      <ProductShowcaseSection
        productShowCaseData={productShowCaseData}
        isFetching={isFetching}
        fetchError={fetchError}
        sentinelRef={sentinelRef}
        onLoadMore={loadMore}
        onRetry={() => {
          setFetchError(null);
          loadMore();
        }}
      />

      <ContactUs
        title={
          <h3 className="font-bold text-2xl text-foreground capitalize text-center">
            We are Promotional Products Now
          </h3>
        }
      />

      <ProtectedSection />

      {/* Sentinel for lazy-loading blog section */}
      <div ref={blogTriggerRef} aria-hidden="true" />

      <BlogSection blogs={blogs} isBlogLoading={isBlogLoading} isBlogSuccess={isBlogSuccess} />

      {/* Spacer so sticky CTA bar does not overlap content on mobile */}
      <div className="h-20 xl:hidden" aria-hidden="true" />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(
            homePageSchema({
              products: loaderData.products,
              productShowCase: loaderData.productShowCase,
            }),
          ),
        }}
      />
    </>
  );
}
